import {
  browserRandomInteger,
  type RandomIntegerSource,
} from '@/shared/random-integer'

const INTEGER_PATTERN = /^[+-]?\d+$/
const DECIMAL_PATTERN = /^[+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+)$/
const MAX_SAFE_BIGINT = BigInt(Number.MAX_SAFE_INTEGER)
const MIN_SAFE_BIGINT = BigInt(Number.MIN_SAFE_INTEGER)

export const MAX_QUANTITY = 1000
export const MAX_DECIMAL_PLACES = 8
export const MAX_BOUND_LENGTH = 256

export type RandomNumberMode = 'integer' | 'decimal'
export type RandomNumberError =
  | 'empty_input'
  | 'invalid_number'
  | 'invalid_range'
  | 'invalid_quantity'
  | 'invalid_precision'
  | 'unique_count_exceeds_capacity'
  | 'unsafe_range'
  | 'processing_failed'

export interface RandomNumberRequest {
  min: string
  max: string
  quantity: string
  mode: RandomNumberMode
  decimalPlaces: string
  unique: boolean
}

export interface ValidatedRandomNumberRequest {
  lower: number
  capacity: number
  quantity: number
  decimalPlaces: number
  unique: boolean
}

export type RandomNumberValidation =
  | { status: 'ready'; value: ValidatedRandomNumberRequest }
  | { status: 'error'; error: RandomNumberError; invalidFields: Array<keyof RandomNumberRequest> }

export type RandomNumberGeneration =
  | { status: 'success'; values: string[] }
  | { status: 'error'; error: RandomNumberError; invalidFields: Array<keyof RandomNumberRequest> }

function error(
  reason: RandomNumberError,
  invalidFields: Array<keyof RandomNumberRequest>,
): RandomNumberValidation {
  return { status: 'error', error: reason, invalidFields }
}

function isSafeBigInt(value: bigint): boolean {
  return value >= MIN_SAFE_BIGINT && value <= MAX_SAFE_BIGINT
}

function parseScaledBound(
  rawValue: string,
  decimalPlaces: number,
  rounding: 'ceil' | 'floor',
): bigint | null {
  const normalized = rawValue.trim().replace(',', '.')
  if (!DECIMAL_PATTERN.test(normalized)) return null

  const negative = normalized.startsWith('-')
  const unsigned = normalized.replace(/^[+-]/, '')
  const [integerPart = '0', fractionPart = ''] = unsigned.split('.')
  const normalizedInteger = integerPart === '' ? '0' : integerPart
  const retainedFraction = fractionPart.slice(0, decimalPlaces).padEnd(decimalPlaces, '0')
  const discardedHasValue = /[1-9]/.test(fractionPart.slice(decimalPlaces))
  const magnitude = BigInt(`${normalizedInteger}${retainedFraction}`)

  if (!negative) {
    return rounding === 'ceil' && discardedHasValue ? magnitude + 1n : magnitude
  }

  return rounding === 'floor' && discardedHasValue ? -(magnitude + 1n) : -magnitude
}

function parseQuantity(value: string): number | null {
  const normalized = value.trim()
  if (!INTEGER_PATTERN.test(normalized)) return null

  const parsed = Number(normalized)
  return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= MAX_QUANTITY ? parsed : null
}

function parseDecimalPlaces(value: string): number | null {
  const normalized = value.trim()
  if (!INTEGER_PATTERN.test(normalized)) return null

  const parsed = Number(normalized)
  return Number.isSafeInteger(parsed) && parsed >= 0 && parsed <= MAX_DECIMAL_PLACES ? parsed : null
}

export function validateRandomNumberRequest(request: RandomNumberRequest): RandomNumberValidation {
  const emptyFields: Array<keyof RandomNumberRequest> = []
  if (request.min.trim() === '') emptyFields.push('min')
  if (request.max.trim() === '') emptyFields.push('max')
  if (request.quantity.trim() === '') emptyFields.push('quantity')
  if (request.mode === 'decimal' && request.decimalPlaces.trim() === '') emptyFields.push('decimalPlaces')
  if (emptyFields.length > 0) return error('empty_input', emptyFields)

  const quantity = parseQuantity(request.quantity)
  if (quantity === null) return error('invalid_quantity', ['quantity'])

  const decimalPlaces = request.mode === 'decimal'
    ? parseDecimalPlaces(request.decimalPlaces)
    : 0
  if (decimalPlaces === null) return error('invalid_precision', ['decimalPlaces'])

  const oversizedBoundaries: Array<keyof RandomNumberRequest> = []
  if (request.min.length > MAX_BOUND_LENGTH) oversizedBoundaries.push('min')
  if (request.max.length > MAX_BOUND_LENGTH) oversizedBoundaries.push('max')
  if (oversizedBoundaries.length > 0) return error('invalid_number', oversizedBoundaries)

  const boundaryPattern = request.mode === 'integer' ? INTEGER_PATTERN : DECIMAL_PATTERN
  const invalidBoundaries: Array<keyof RandomNumberRequest> = []
  if (!boundaryPattern.test(request.min.trim())) invalidBoundaries.push('min')
  if (!boundaryPattern.test(request.max.trim())) invalidBoundaries.push('max')
  if (invalidBoundaries.length > 0) return error('invalid_number', invalidBoundaries)

  const lower = parseScaledBound(request.min, decimalPlaces, 'ceil')
  const upper = parseScaledBound(request.max, decimalPlaces, 'floor')
  if (lower === null || upper === null) return error('invalid_number', ['min', 'max'])
  if (!isSafeBigInt(lower) || !isSafeBigInt(upper)) return error('unsafe_range', ['min', 'max'])
  if (lower > upper) return error('invalid_range', ['min', 'max'])

  const capacity = upper - lower + 1n
  if (capacity > MAX_SAFE_BIGINT) return error('unsafe_range', ['min', 'max'])
  if (request.unique && BigInt(quantity) > capacity) {
    return error('unique_count_exceeds_capacity', ['quantity', 'unique'])
  }

  return {
    status: 'ready',
    value: {
      lower: Number(lower),
      capacity: Number(capacity),
      quantity,
      decimalPlaces,
      unique: request.unique,
    },
  }
}

function sampleGridIndexes(
  capacity: number,
  quantity: number,
  unique: boolean,
  randomInteger: RandomIntegerSource,
): number[] {
  if (!unique) {
    return Array.from({ length: quantity }, () => randomInteger(capacity))
  }

  const swaps = new Map<number, number>()
  const selected: number[] = []

  for (let index = 0; index < quantity; index += 1) {
    const remaining = capacity - index
    const pickedPosition = randomInteger(remaining)
    const pickedValue = swaps.get(pickedPosition) ?? pickedPosition
    const lastPosition = remaining - 1
    const replacement = swaps.get(lastPosition) ?? lastPosition

    selected.push(pickedValue)
    swaps.set(pickedPosition, replacement)
    swaps.delete(lastPosition)
  }

  return selected
}

function formatGridValue(scaledValue: number, decimalPlaces: number): string {
  if (decimalPlaces === 0) return String(scaledValue)

  const factor = 10 ** decimalPlaces
  const negative = scaledValue < 0
  const magnitude = Math.abs(scaledValue)
  const integerPart = Math.floor(magnitude / factor)
  const fractionPart = String(magnitude % factor).padStart(decimalPlaces, '0')

  return `${negative ? '-' : ''}${integerPart}.${fractionPart}`
}

export function generateRandomNumbers(
  request: RandomNumberRequest,
  randomInteger: RandomIntegerSource = browserRandomInteger,
): RandomNumberGeneration {
  const validation = validateRandomNumberRequest(request)
  if (validation.status === 'error') return validation

  const { lower, capacity, quantity, decimalPlaces, unique } = validation.value
  try {
    const indexes = sampleGridIndexes(capacity, quantity, unique, randomInteger)

    return {
      status: 'success',
      values: indexes.map((index) => formatGridValue(lower + index, decimalPlaces)),
    }
  } catch {
    return { status: 'error', error: 'processing_failed', invalidFields: [] }
  }
}
